import type { StepUpDetail, StepUpLabel } from '../store'
import { useId, useState } from 'react'
import { Dialog, Modal, ModalOverlay } from 'react-aria-components'
import { Button } from '@/components/base/buttons/button'
import { Input } from '@/components/base/input/input'
import { api } from '../api/client'
import { useShell } from '../store'
import { stepUpErrorMessage } from './step-up-error'

/**
 * PRD-019 §3.6 / PRD-018 §3.10: the shell owns the step-up modal. It states what is being authorized, verifies a
 * fresh TOTP code through POST /auth/step-up, and resolves the caller's promise. It sits above every other layer
 * (z-[55]) so it opens on top of a confirmation dialog. Failures are named by class (step-up-error.ts): a wrong code,
 * a dead session, a refused session, or an API/network fault.
 *
 * PRD-016 §7.18 (UX audit 2026-09-19 P1-4): the body is a box stating exactly what is being authorized, action and
 * object, with the amount and the company when the action posts to the books ("Approve and post · Journal entry ·
 * Inventory true-up · $2,140.00 · Northwind Retail LLC"), and the confirm button names the action ("Confirm and
 * post"). Callers that pass a plain string get the older one-line form. Prior art: Stripe's confirm dialogs name the
 * verb, the object, the amount and the account before the button; GitHub's "sudo mode" names the action it protects.
 */
export function stepUpDetail(label: StepUpLabel): StepUpDetail {
  return typeof label === 'string' ? { action: label, object: '' } : label
}

/** The one line the box reads as, for the record and for tests: action · object · amount · company. */
export function stepUpSummary(d: StepUpDetail): string {
  return [d.action, d.object, d.memo, d.amount, d.company].filter((x): x is string => Boolean(x)).join(' · ')
}

export function StepUpModal() {
  const { stepUp, resolveStepUp } = useShell()
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const titleId = useId()
  if (!stepUp)
    return null
  const detail = stepUpDetail(stepUp.label)
  const structured = detail.object.length > 0
  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api('/auth/step-up', { method: 'POST', json: { code, action: stepUp.action } })
      setCode('')
      resolveStepUp(true)
    }
    catch (e) {
      const { message, clearCode } = stepUpErrorMessage(e)
      setError(message)
      if (clearCode)
        setCode('')
    }
    finally {
      setBusy(false)
    }
  }
  return (
    <ModalOverlay isOpen isDismissable onOpenChange={o => !o && resolveStepUp(false)} className="fixed inset-0 z-[55] flex items-center justify-center bg-overlay/40 p-4">
      <Modal className="w-full max-w-sm rounded-xl bg-primary p-6 shadow-xl">
        <Dialog aria-labelledby={titleId} className="outline-none" data-testid="stepup-dialog">
          <h2 id={titleId} className="text-lg font-semibold text-primary">Confirm with your authenticator</h2>
          {structured
            ? (
                <dl className="mt-3 rounded-lg border border-secondary bg-secondary px-3 py-2 text-sm" data-testid="stepup-action" aria-label={stepUpSummary(detail)}>
                  <div className="flex items-baseline justify-between gap-3">
                    <dt className="sr-only">Action</dt>
                    <dd className="font-semibold text-primary" data-testid="stepup-action-verb">{detail.action}</dd>
                    {detail.amount ? <dd className="shrink-0 font-semibold tabular-nums text-primary" data-testid="stepup-amount">{detail.amount}</dd> : null}
                  </div>
                  <dt className="sr-only">Object</dt>
                  <dd className="text-secondary" data-testid="stepup-object">{detail.object}</dd>
                  {detail.memo ? <dd className="text-tertiary" data-testid="stepup-memo">{detail.memo}</dd> : null}
                  {detail.company ? <dd className="text-tertiary" data-testid="stepup-company">{detail.company}</dd> : null}
                  {detail.lines?.map(l => <dd key={l} className="text-tertiary">{l}</dd>)}
                </dl>
              )
            : <p className="mt-1 text-sm text-tertiary" data-testid="stepup-action">{detail.action}</p>}
          <p className="mt-2 text-xs text-quaternary">{stepUp.tier === 'tier2' ? 'This action asks for a code every time.' : 'Tier 1 step-up: a fresh check opens a 15-minute window for similar actions. The check is logged with what it authorized.'}</p>
          <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
            <Input label="Six-digit code" inputMode="numeric" autoComplete="one-time-code" name="code" autoFocus value={code} onChange={v => setCode(v.replace(/\D/g, '').slice(0, 6))} />
            {error ? <p role="alert" className="text-sm text-error-primary">{error}</p> : null}
            <div className="flex justify-end gap-2">
              <Button type="button" color="secondary" onClick={() => resolveStepUp(false)}>Cancel</Button>
              <Button type="submit" isLoading={busy} isDisabled={code.length !== 6} data-testid="stepup-confirm">{detail.confirm ?? 'Confirm'}</Button>
            </div>
          </form>
        </Dialog>
      </Modal>
    </ModalOverlay>
  )
}
